import { zodResolver } from "@hookform/resolvers/zod";
import { KikGenericResponse, KikRestVerbs } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { SvcGetReturnReasonsByType } from "@springtree/eva-services-core-management";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { ChangeEvent, Fragment, useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import IconUneditable from "@/icons/icon-uneditable";
import CartProductTile from "~/components/cart/cart-product-tile";
import Button from "~/components/common/button";
import HighlightedText from "~/components/common/highlighted-text";
import Icon from "~/components/common/icon";
import myaccount from "~/components/common/myaccount.module.scss";
import OverlayGeneric from "~/components/common/overlays/overlay-generic";
import UploadImages from "~/components/common/upload-images";
import Checkbox, { CheckboxVariants } from "~/components/form/checkbox";
import InputField from "~/components/form/input-field";
import InputRadioCheckbox from "~/components/form/input-radio-checkbox";
import SelectGeneric from "~/components/form/select-generic";
import orderDetail from "~/components/myaccount/orders/order-details.module.scss";
import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import { useGuestTrackYourOrder } from "~/hooks/use-guest-track-your-order";
import { HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { StorageFactory } from "~/services/storage.factory";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ApiUtils } from "~/shared/api/api-utils.class";
import { CacheKeys } from "~/shared/cache-keys";
import { GuestTrackOrderSummary } from "~/shared/guest-track-order/guest-track-order-summary";
import Constants from "~/utils/constants";
import productUtils from "~/utils/product-utils";

import styles from "../myaccount/returns/request-return.module.scss";

type EncodedImage = {
  base64: string;
  type: string;
};

export default function GuestReturnYourOrderStep2({ onPrev }: { onPrev: () => void }) {
  const t = useTranslations();
  const router = useRouter();
  const { guestTrackingToken, setGuestTrackingToken } = useAuthContext();
  const sessionCache = useMemo(() => new StorageFactory("sessionStorage"), []);
  const getReturnReasonsByType = useEvaService(SvcGetReturnReasonsByType);
  const { newErrorGlobalNotification } = useGlobalNotification();

  const queryreasons = useQuery({
    queryKey: ["guestGetReturnReasons"],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await getReturnReasonsByType({
        Type: 1,
      });

      return data?.Reasons?.filter(
        (reason) => reason.BackendID && !Constants.EXCLUDED_RETURN_REASONS.includes(reason.BackendID)
      );
    },
    enabled: true,
  });
  const queryorder = useGuestTrackYourOrder();

  const isInvalidGuestTokenError = useMemo(() => {
    if (!queryorder.error) {
      return false;
    }

    const errorResponse = ApiUtils.errorFactory(queryorder.error as any);
    const status =
      (queryorder.error as any)?.response?.status ?? (queryorder.error as any)?.status ?? errorResponse.status;

    return status === 401 || status === 403;
  }, [queryorder.error]);

  useEffect(() => {
    if (!isInvalidGuestTokenError) {
      return;
    }

    newErrorGlobalNotification(
      {
        title: "generic.error",
        description: t("generic.expiredOrInvalidToken"),
      },
      { timeout: 7500 }
    );
    setGuestTrackingToken(undefined);
    onPrev();
  }, [isInvalidGuestTokenError, newErrorGlobalNotification, onPrev, setGuestTrackingToken, t]);

  const validationSchema = z.object({
    products: z
      .object({
        selected: z.boolean(),
        OrderLineID: z.string(),
        ReturnableQuantity: z.number().min(1).optional(),
        refundReason: z.string().optional(),
        additionalInfo: z.string().optional(),
        images: z.object({ base64: z.string(), type: z.string() }).array().optional(),
      })
      .refine(
        (val) => {
          if (!val.selected) {
            return true;
          }

          const line = queryorder.data?.returnableStatuses?.find(
            (prd) => `${prd.OrderLineID}` === `${val.OrderLineID}`
          );
          return (val.ReturnableQuantity ?? 0) <= (line?.ReturnableQuantity ?? 0);
        },
        (val) => {
          const line = queryorder.data?.returnableStatuses?.find(
            (prd) => `${prd.OrderLineID}` === `${val.OrderLineID}`
          );
          return {
            message: `max quantity is ${line?.ReturnableQuantity ?? 0} for selected product`,
            path: ["ReturnableQuantity"],
          };
        }
      )
      .array()
      .refine((arr) => arr.some((line) => line.selected), t("generic.chooseRefoundProducts")),
    refundMethod: z.literal("coupon").or(z.literal("original")),
  });

  type ValidationSchema = z.infer<typeof validationSchema>;

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ValidationSchema>({
    ...Constants.USE_FORM_VALIDATION_DEFAULTS,
    resolver: zodResolver(validationSchema),
    defaultValues: {
      refundMethod: "original",
    },
  });

  const watchproducts = watch("products");

  const isReturnable = (line: any) => {
    const product = queryorder.data?.returnableStatuses?.find((el) => `${el.OrderLineID}` === `${line.ID}`);
    return !!(product?.IsReturnable && !productUtils.isAVirtualishOrServiceProduct(line?.Product?.Properties as any));
  };

  const toggleAll = (e: ChangeEvent<HTMLInputElement>) => {
    queryorder.data?.products?.forEach((line, index) => {
      if (isReturnable(line)) {
        setValue(`products.${index}.selected`, e.target.checked);
      }
    });
  };

  const { allNotReturnable, hasLoyaltyProgram, isUserSubscribedToLoyalty } = useMemo(() => {
    const products = queryorder.data?.products ?? [];
    const loyalty = queryorder.data?.loyalty;
    return {
      allNotReturnable: products.every((line) => !isReturnable(line)),
      hasLoyaltyProgram: !!loyalty?.hasLoyaltyProgram,
      isUserSubscribedToLoyalty: (loyalty?.userSubscriptions || []).some((subscription) => subscription.Status === 2),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryorder.data]);

  const onSubmit: SubmitHandler<ValidationSchema> = async (data) => {
    try {
      const selectedLines = data.products.filter((prd) => prd.selected);

      const response = await HttpService.$http<KikGenericResponse<GuestTrackOrderSummary>>({
        url: ApiConstants.endpointsMapiV1.ecoGuestTrackOrderReturn,
        method: KikRestVerbs.POST,
        headers: {
          Authorization: `Bearer ${guestTrackingToken}`,
        },
        body: {
          CreateInOriginalOrganizationUnit: true,
          ReturnWithoutProducts: false,
          Lines: selectedLines.map((selectedLine) => ({
            OrderLineID: selectedLine.OrderLineID,
            Quantity: selectedLine.ReturnableQuantity!,
            ReturnReasonID: selectedLine.refundReason!,
            CustomFields: {
              additionalInfo: { StringValue: selectedLine.additionalInfo },
            },
            Images: selectedLine.images,
          })),
        },
      });

      if (response?.data?.orderDetail?.Result?.ID) {
        const guestReturnOrderId = response.data.orderDetail.Result.ID;
        sessionCache.set(CacheKeys.GUEST_RETURN_THANK_YOU_STORAGE_KEY, {
          orderId: guestReturnOrderId,
          data: response.data,
        });
        setGuestTrackingToken(undefined);
        await router.push(`/thankyou?order=${guestReturnOrderId}&success=true&guestReturn=true`);
        return;
      }

      newErrorGlobalNotification(
        {
          title: "generic.error",
          description: t("generic.error"),
        },
        { timeout: 7500 }
      );
    } catch (error: any) {
      Logger.instance.warn("GuestReturnYourOrderStep2: submit error", error);
      const errorResponse = ApiUtils.errorFactory(error);

      newErrorGlobalNotification(
        {
          title: "generic.error",
          description: errorResponse.messages?.[0]?.message || t("generic.error"),
        },
        { timeout: 7500 }
      );
    }
  };

  if (queryorder.isLoading || queryreasons.isLoading) {
    return <OverlayGeneric />;
  }

  if (isInvalidGuestTokenError) {
    return null;
  }

  if (!queryorder.data || queryorder.error) {
    return <p className={myaccount.infoMessage}>{t("generic.couldntGetOrderDetails")}</p>;
  }

  if (!queryreasons.data || queryreasons.error) {
    return <p className={myaccount.infoMessage}>{t("generic.error")}</p>;
  }

  const orderData = queryorder.data;
  const reasonsData = queryreasons.data;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={orderDetail.orderDetailsWrapper}>
      <div className={orderDetail.centeredTitle}>
        <Button variant="primary" type="button" onClick={onPrev}>
          {t("generic.back")}
        </Button>
      </div>
      <div className={orderDetail.centeredTitle}>
        <h2>
          <HighlightedText text={t("generic.yourreturnrequest")} />
        </h2>
      </div>

      {hasLoyaltyProgram && isUserSubscribedToLoyalty && (
        <div className={classNames(styles.alertBox, myaccount.myaccountCard)}>
          <p className={myaccount.infoMessage}>{t("generic.returns.looseloyalitypoints")}</p>
        </div>
      )}

      <div className={myaccount.myaccountCard}>
        <div className={myaccount.myaccountCardHeader}>
          <h3>{t("generic.chooseRefoundProducts")}</h3>
        </div>
        <div className={myaccount.myaccountCardContent}>
          <Checkbox
            label={t("generic.selectAll")}
            variant={CheckboxVariants.toggle}
            onChange={(e) => {
              toggleAll(e);
              clearErrors();
            }}
            className={styles.selectAll}
            disabled={allNotReturnable}
          />
          <div className={styles.orderItems}>
            {orderData.products?.map((line, index) => {
              const returnStatusLine = orderData.returnableStatuses?.find((el) => `${el.OrderLineID}` === `${line.ID}`);
              const watchCurrent = watchproducts?.find((el) => `${el.OrderLineID}` === `${line.ID}`);
              const selected = watchproducts?.some((el) => el?.selected && `${el.OrderLineID}` === `${line.ID}`);
              const isAVirtualishOrServiceProduct =
                line?.Product?.Properties &&
                productUtils.isAVirtualishOrServiceProduct(line?.Product?.Properties as any);
              const isReturnableStatus = isReturnable(line);
              const returnableQuantity = returnStatusLine?.ReturnableQuantity ?? 0;
              const orderLineId = String(returnStatusLine?.OrderLineID ?? line.ID);
              const notReturnableReason = isAVirtualishOrServiceProduct
                ? t("generic.returns.notReturnable")
                : returnStatusLine?.Reason || t("generic.returns.notReturnable");

              return (
                <Fragment key={line.ID}>
                  <input {...register(`products.${index}.OrderLineID`)} type="hidden" value={orderLineId} />
                  <div className={styles.divider} aria-hidden />
                  {!isReturnableStatus && <p className={styles.infoBox}>{notReturnableReason}</p>}
                  <div className={classNames(styles.customLabel, { [styles.disabled]: !isReturnableStatus })}>
                    {isReturnableStatus ? (
                      <>
                        <InputRadioCheckbox
                          type="checkbox"
                          {...register(`products.${index}.selected`, {
                            onChange: () => {
                              clearErrors();
                            },
                          })}
                          error={errors.products?.[index]?.message}
                          onClearErrors={() => clearErrors(`products.${index}`)}
                        />
                        <input
                          type="hidden"
                          {...register(`products.${index}.ReturnableQuantity`, { valueAsNumber: true })}
                          defaultValue={returnableQuantity}
                        />
                      </>
                    ) : (
                      <>
                        <input type="hidden" {...register(`products.${index}.selected`, { value: false })} />
                        <Icon icon={IconUneditable} width={16} height={16} />
                      </>
                    )}
                    <CartProductTile
                      line={line}
                      quantity={watchCurrent?.ReturnableQuantity}
                      disableAdd={!((watchCurrent?.ReturnableQuantity ?? 0) < returnableQuantity)}
                      disableRemove={!((watchCurrent?.ReturnableQuantity ?? 0) > 1)}
                      toggleQuantity={(removing) => {
                        setValue(
                          `products.${index}.ReturnableQuantity`,
                          (watchCurrent?.ReturnableQuantity ?? 0) + (removing ? -1 : 1)
                        );
                      }}
                      disableCartQty={!selected}
                      disableCartActions
                      className={styles.tile}
                      showNetPriceWithoutPromo
                    />
                  </div>
                  {selected && (
                    <>
                      <SelectGeneric
                        {...register(`products.${index}.refundReason`)}
                        label={t("generic.returns.why")}
                        options={reasonsData.map((res) => ({ value: res.ID, label: res.Name || "" }))}
                        className={styles.selectReason}
                      />
                      <UploadImages
                        setImages={(images: EncodedImage[]) => setValue(`products.${index}.images`, images)}
                      />
                      <p className={styles.reg}>{t("generic.returns.tellmore")}</p>
                      <InputField
                        {...register(`products.${index}.additionalInfo`)}
                        className={styles.additionalInfo}
                        label={t("generic.returns.additional")}
                        isOptional
                      />
                    </>
                  )}
                </Fragment>
              );
            })}
            {errors.products && (
              <p className={styles.errorMessage}>{errors.products?.message ?? errors.products?.root?.message}</p>
            )}
          </div>
        </div>
      </div>

      <Button
        variant="primary"
        type="submit"
        disabled={allNotReturnable || isSubmitting}
        loading={isSubmitting}
        className={styles.centeredButton}
      >
        {t("generic.returns.send")}
      </Button>
    </form>
  );
}
